import { Command, defaultFilter } from "cmdk";
import { useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { matchPath, useLocation, useNavigate } from "react-router";
import { addMonthsIso, formatMonth, live, monthOf, todayIso } from "@budgeter/shared";
import { useSnapshot } from "../../data/StoreContext";
import { categoryGroups } from "../../data/selectors";

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  keywords?: string[];
  to: string;
}

interface PaletteGroup {
  heading: string;
  items: PaletteItem[];
}

/** The reports (SPEC §12) and settings pages the palette can jump to. */
export const reportPages: { path: string; label: string }[] = [
  { path: "/reports/spending", label: "Spending" },
  { path: "/reports/income-expense", label: "Income vs expense" },
  { path: "/reports/net-worth", label: "Net worth" },
];

export const settingsPages: { path: string; label: string; keywords?: string[] }[] = [
  { path: "/settings", label: "Settings" },
  { path: "/settings/categories", label: "Categories", keywords: ["hide", "reorder"] },
  { path: "/settings/accounts", label: "Accounts", keywords: ["close", "rename"] },
  { path: "/settings/payees", label: "Payees and rules", keywords: ["rules", "merge"] },
  {
    path: "/settings/scheduled",
    label: "Scheduled transactions",
    keywords: ["scheduled", "recurring", "repeat", "bills"],
  },
  { path: "/settings/memories", label: "What Claude remembers", keywords: ["memories", "claude"] },
  { path: "/settings/history", label: "History", keywords: ["undo", "restore", "changes"] },
];

/**
 * cmdk's fuzzy score, except that a label starting with the query, or with a word that does,
 * outranks every scattered-letter match ("gro" also hits "Budget: October … this month").
 */
function paletteScore(query: string, item: PaletteItem): number {
  const fuzzy = defaultFilter("", query, [item.label, ...(item.keywords ?? []).filter(Boolean)]);
  const q = query.trim().toLowerCase();
  const label = item.label.toLowerCase();
  if (fuzzy === 0) {
    return 0;
  }
  if (label.startsWith(q)) {
    return 1;
  }
  if (label.split(/[\s:]+/).some((word) => word.startsWith(q))) {
    return 0.9;
  }
  return fuzzy * 0.5;
}

const monthsBack = 12;
const monthsAhead = 6;

/**
 * ⌘K / Ctrl+K (SPEC §11.2): jump to an account, budget month, category, report or settings
 * page; start an import; reconcile. Fuzzy matching is cmdk's. Mount once inside the router.
 */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const s = useSnapshot();

  useHotkeys(
    "mod+k",
    (e) => {
      e.preventDefault();
      setSearch("");
      setOpen((o) => !o);
    },
    { enableOnFormTags: true, enableOnContentEditable: true },
  );

  const currentAccount = matchPath("/accounts/:accountId", pathname)?.params.accountId;

  const groups = useMemo((): PaletteGroup[] => {
    const accounts = live(s, "accounts")
      .filter((a) => !a.closed)
      .sort(
        (a, b) =>
          Number(b.onBudget) - Number(a.onBudget) ||
          a.sortOrder - b.sortOrder ||
          a.name.localeCompare(b.name),
      );
    const current = accounts.find((a) => a.id === currentAccount);
    const reconcilable = current ? [current, ...accounts.filter((a) => a !== current)] : accounts;
    const thisMonth = monthOf(todayIso());
    // This month first, then outwards: last month, next month, …
    const offsets = [0];
    for (let i = 1; i <= monthsBack; i++) {
      offsets.push(-i);
      if (i <= monthsAhead) {
        offsets.push(i);
      }
    }
    const months: PaletteItem[] = offsets.map((i) => {
      const m = addMonthsIso(thisMonth, i);
      return {
        id: `month-${m}`,
        label: `Budget: ${formatMonth(m)}`,
        hint: i === 0 ? "This month" : undefined,
        keywords: [m, formatMonth(m, "short"), i === 0 ? "this month" : ""],
        to: `/budget/${m}`,
      };
    });
    return [
      {
        heading: "Actions",
        items: [
          {
            id: "import",
            label: "Start an import",
            keywords: ["upload", "ofx", "csv"],
            to: "/import",
          },
          ...reconcilable.map((a) => ({
            id: `reconcile-${a.id}`,
            label: `Reconcile ${a.name}`,
            hint: a.id === currentAccount ? "This account" : undefined,
            to: `/accounts/${a.id}?reconcile=1`,
          })),
        ],
      },
      {
        heading: "Accounts",
        items: [
          ...accounts.map((a) => ({
            id: `account-${a.id}`,
            label: a.name,
            hint: a.onBudget ? "Account" : "Tracking account",
            keywords: ["account", "register"],
            to: `/accounts/${a.id}`,
          })),
          { id: "account-all", label: "All accounts", keywords: ["register"], to: "/accounts/all" },
        ],
      },
      { heading: "Budget", items: months },
      {
        heading: "Categories",
        items: categoryGroups(s).flatMap((g) =>
          g.categories.map((c) => ({
            id: `category-${c.id}`,
            label: c.name,
            hint: g.master.name,
            keywords: [g.master.name, "category"],
            to: `/category/${c.id}`,
          })),
        ),
      },
      {
        heading: "Reports",
        items: reportPages.map((r) => ({
          id: `report-${r.path}`,
          label: `Report: ${r.label}`,
          keywords: ["report"],
          to: r.path,
        })),
      },
      {
        heading: "Settings",
        items: settingsPages.map((p) => ({
          id: `settings-${p.path}`,
          label: p.label,
          hint: p.path === "/settings" ? undefined : "Settings",
          keywords: ["settings", ...(p.keywords ?? [])],
          to: p.path,
        })),
      },
    ];
  }, [s, currentAccount]);

  // cmdk 1.1 sorts items within a group but never reorders the groups themselves, so a
  // weak month match would stay above a category named by the query: rank here instead.
  const ranked = useMemo((): PaletteGroup[] => {
    if (search.trim() === "") {
      return groups;
    }
    return groups
      .map((g) => {
        const items = g.items
          .map((item) => ({ item, score: paletteScore(search, item) }))
          .filter((x) => x.score > 0)
          .sort((a, b) => b.score - a.score);
        return { g, items, best: items[0]?.score ?? 0 };
      })
      .filter((x) => x.items.length > 0)
      .sort((a, b) => b.best - a.best)
      .map((x) => ({ heading: x.g.heading, items: x.items.map((i) => i.item) }));
  }, [groups, search]);

  const go = (to: string) => {
    setOpen(false);
    navigate(to);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={setOpen}
      label="Command palette"
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-50 bg-ink/30"
      contentClassName="fixed top-[12vh] left-1/2 z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 overflow-hidden rounded-[16px] border border-line bg-surface shadow-[0_10px_30px_rgba(51,41,31,.16)]"
    >
      <Command.Input
        value={search}
        onValueChange={setSearch}
        placeholder="Jump to an account, month, category, report…"
        className="h-[52px] w-full border-0 border-b border-line bg-transparent px-4 text-[16px] text-ink outline-none placeholder:text-faint"
      />
      <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
        <Command.Empty className="px-3 py-6 text-center text-[14px] text-muted">
          Nothing matches.
        </Command.Empty>
        {ranked.map((g) => (
          <Command.Group
            key={g.heading}
            heading={g.heading}
            className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[11.5px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:tracking-[.5px] [&_[cmdk-group-heading]]:text-muted [&_[cmdk-group-heading]]:uppercase"
          >
            {g.items.map((item) => (
              <Command.Item
                key={item.id}
                value={item.id}
                onSelect={() => go(item.to)}
                className="flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-[14.5px] data-[selected=true]:bg-accent-soft"
              >
                <span className="truncate">{item.label}</span>
                {item.hint && (
                  <span className="shrink-0 text-[12.5px] text-muted">{item.hint}</span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        ))}
      </Command.List>
    </Command.Dialog>
  );
}
